/**
 * PlatformHeader — THE SINGLE GLOBAL HEADER SNIPPET (Rule #4 — iter 240).
 *
 * One header, every module, every domain. PWA + native wrappers all render
 * the same top strip. Modules pass props to swap brand / nav / CTA; the
 * language picker, theme toggle, account and cart are baked in and always
 * consistent.
 *
 * NEVER duplicate this component. If a module needs a variation, add a
 * prop to this file. See /app/memory/AGENT_RULES.md.
 *
 * Props (all optional — defaults come from BusinessContext + SettingsContext):
 *   brand         { name, to?, logoSrc?, subtitle? } — override the brand block.
 *   nav           Array<{to,label}|{label,children:[{to,label}]}> — override nav.
 *   ctaLabel      string
 *   ctaLink       string
 *   showBookCta   boolean (default: business.features.table_bookings !== false)
 *   showCart      boolean (default: true)
 *   showAccount   boolean (default: true)
 *   showLanguage  boolean (default: business.features.language_selector !== false)
 *   showTheme     boolean (default: true)
 *   extras        ReactNode — rendered between brand and right-side controls.
 *   belowContent  ReactNode — rendered inside the sticky container beneath
 *                 the top strip (module-specific sticky rows: niche tabs,
 *                 district tabs, search bar…).
 *   maxWidth      Tailwind class (default: "max-w-7xl") — DirectoryPage uses
 *                 "max-w-6xl" for a tighter dense-info layout.
 *   showEmergency boolean (default: true) — Emergency dropdown (Police /
 *                 Ambulance / Fire / Nearest hospital) inside the top strip.
 *   showModules   boolean (default: true) — "Other Modules" dropdown that
 *                 lists every enabled module from /api/platform/modules.
 *   userLoc       {lat,lng} — passed through to EmergencyMenu for nearest-
 *                 hospital lookup.
 */
import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ShoppingBag, Menu as MenuIcon, X, User } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { useBusiness } from "@/context/BusinessContext";
import CartSheet from "@/components/CartSheet";
import LanguagePicker from "@/components/LanguagePicker";
import ThemeToggle from "@/components/ThemeToggle";
import Tip from "@/components/Tip";
import EmergencyMenu from "@/components/EmergencyMenu";
import ModulesMenu from "@/components/ModulesMenu";
import GrandfatherLockBadge from "@/components/GrandfatherLockBadge";
import AccountHeader from "@/components/AccountHeader";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/useT";
import { getModuleHost } from "@/lib/moduleHost";
import { isMeetupsHost } from "@/lib/moduleHeaderConfig";
import { api } from "@/lib/api";

function NavDropdown({ group, location }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e)  { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);
  const activeChild = (group.children || []).find((c) => c.to === location.pathname);
  const slug = group.label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        data-testid={`nav-group-${slug}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 text-sm tracking-wide transition-colors ${
          activeChild ? "text-[#C05746]" : "text-[#121614] dark:text-stone-100 hover:text-[#C05746]"
        }`}
      >
        {group.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2} />
      </button>
      {open && (
        <div data-testid={`nav-group-${slug}-menu`} className="absolute left-1/2 -translate-x-1/2 top-full pt-2 min-w-[14rem] z-50">
          <div className="rounded-xl border border-[#E8E5DF] dark:border-stone-700 bg-white dark:bg-stone-900 shadow-xl py-2">
            {(group.children || []).map((c) => (
              <Link
                key={c.to}
                to={c.to}
                data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`block px-4 py-2 text-sm transition-colors ${
                  location.pathname === c.to
                    ? "text-[#C05746] bg-[#FDFBF7] dark:bg-stone-800"
                    : "text-[#121614] dark:text-stone-100 hover:bg-[#F3F0EA] dark:hover:bg-stone-800"
                }`}
              >
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PlatformHeader({
  brand,
  nav,
  ctaLabel,
  ctaLink,
  showBookCta,
  showCart = true,
  showAccount = true,
  showLanguage,
  showTheme = true,
  showEmergency = true,
  showModules = true,
  userLoc = null,
  extras = null,
  belowContent = null,
  maxWidth = "max-w-7xl",
  salesTopGap = false,
}) {
  const { totalItems } = useCart();
  const settings = useSettings();
  const { user } = useAuth();
  const { business } = useBusiness();
  const { t } = useTranslation();
  const tr = useT();
  const location = useLocation();

  const moduleBrand = typeof window !== "undefined" ? getModuleHost() : null;
  const isRecipesHost = moduleBrand?.key === "recipes";
  // Any module host (finder, plaza, travel, dictate, suite, recipes, etc)
  // should show the module brand — NEVER the tenant's demo copy.
  // The tenant name is only relevant on their own storefront.
  const isModuleHost = !!moduleBrand?.title;

  // Resolve brand
  //  • On /account/* the header should say "HustleSuite · My Account"
  //    NOT the tenant's storefront — the member is managing their
  //    OWN account, not shopping the tenant's storefront.
  const onAccountPage = typeof window !== "undefined"
    && (window.location.pathname === "/account"
        || window.location.pathname.startsWith("/account/"));
  const resolvedBrand = brand ?? (onAccountPage
    ? { name: moduleBrand?.title || "HustleSuite · My Account", to: "/account" }
    : {
        name: moduleBrand?.title
          || business?.business_name
          || settings?.restaurant_name
          || "HustleSuite",
        to: moduleBrand?.home || "/",
      });

  // Resolve CTA — module hosts don't get the tenant's "Reserve" button.
  const featBookings = business?.features?.table_bookings !== false;
  const resolvedShowBookCta = showBookCta ?? (isModuleHost ? false : featBookings);
  const resolvedCtaLabel = ctaLabel ?? (isRecipesHost
    ? "Explore recipes"
    : (tr(business, "cta_label") || "Reserve"));
  const resolvedCtaLink = ctaLink ?? (isRecipesHost ? "/recipes" : (business?.cta_link || "/book"));

  // Resolve nav
  const customNav = Array.isArray(business?.custom_nav) ? business.custom_nav : [];
  const navMode = business?.nav_mode || "merge";
  // Module hosts (finder, plaza, travel, dictate…) MUST NOT inherit
  // the tenant's restaurant nav. Recipes has a bespoke nav; every
  // other module falls back to just the module home link.
  const defaultNav = isRecipesHost
    ? [
        { to: "/recipes", label: "Recipes" },
        { to: "/recipe-planner", label: "Meal Planner" },
        { to: "/my-cookbook", label: "My Cookbook" },
      ]
    : isModuleHost
    ? [
        { to: moduleBrand.home || "/", label: "Home" },
      ]
    : navMode === "custom" && customNav.length > 0
    ? customNav
    : [
        { to: "/", label: "Home" },
        { to: "/menu", label: t("nav_menu") },
        ...(featBookings ? [{ to: "/book", label: t("nav_book") }] : []),
        { to: "/meetup", label: "Meetup" },
        ...customNav,
        { to: "/gallery", label: "Gallery" },
        { to: "/reviews", label: t("nav_reviews") },
      ];
  const NAV = nav ?? defaultNav;

  // Resolve language flag
  const resolvedShowLanguage = showLanguage ?? (business?.features?.language_selector !== false);

  const [cartOpen, setCartOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Module-driven header tabs (currently Meetups only — read from
  // /api/hustlemeet-settings on hustlemeetups.com*). Other modules
  // fall back to the passed `nav` prop mapped into the same shape,
  // so every module can render the same cream module header without
  // needing a per-host backend settings row.
  const [settingsModuleTabs, setSettingsModuleTabs] = useState([]);
  useEffect(() => {
    if (!isMeetupsHost()) return;
    let cancelled = false;
    api.get("/hustlemeet-settings")
      .then((r) => {
        if (cancelled) return;
        const items = Array.isArray(r?.data?.header_menu_items) ? r.data.header_menu_items : [];
        setSettingsModuleTabs(items.filter((it) => it && it.enabled !== false && it.path));
      })
      .catch(() => { /* silent — tabs stay empty */ });
    return () => { cancelled = true; };
  }, []);

  // Effective tabs: prefer settings-driven (Meetups) → fall back to
  // the `nav` prop shape so Dictate/Travel/etc drive their own header
  // directly from the sales page.
  const moduleTabs = useMemo(() => {
    if (settingsModuleTabs.length > 0) return settingsModuleTabs;
    if (isModuleHost && Array.isArray(nav) && nav.length > 0) {
      return nav.map((n) => ({
        key: n.key || String(n.to || n.label || "tab").replace(/\W+/g, "-") || "tab",
        label: n.label,
        path: n.to,
        external: n.external ?? /^https?:\/\//i.test(String(n.to || "")),
        align: n.align,
        variant: n.variant,
        children: Array.isArray(n.children) ? n.children : undefined,
        enabled: true,
      }));
    }
    return [];
  }, [settingsModuleTabs, isModuleHost, nav]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // iter268: /account/* renders a SLIM AccountHeader (Rule #4 —
  // still exactly one <PlatformHeader/> mount per page). The module
  // Menu / Book-a-Table / cart / hamburger are all hidden here — the
  // member is managing their own profile, not shopping.
  if (onAccountPage) {
    return (
      <>
        <AccountHeader />
        {showCart && <CartSheet open={cartOpen} onOpenChange={setCartOpen} />}
      </>
    );
  }

  return (
    <>
      {/* iter247-f: On MODULE HOSTS (hustletravel.au, hustlefind.au,
       *  hustleshop.au, hustlemeetups.com, etc.) collapse the whole
       *  PlatformHeader down to the cream strip carrying the module
       *  title + the settings-driven moduleTabs (Home/Meet/Circles/
       *  Matchmaker + right-aligned Join/Login pills for Meetups).
       *  Everything else (cart, hamburger, account/lang/theme) is
       *  either in the black GoogleTranslateBar above OR was pure
       *  cruft on a phone-app screen. Storefronts keep the full-fat
       *  header in the else branch below. */}
      {isModuleHost ? (
        <>
          {/* iter252: Mobile-first module header.
           *  - <xl (< 1280px)     : brand + hamburger → vertical drawer
           *  - >= xl              : brand + inline left-nav + right-pills
           *  Every label uses whitespace-nowrap so translated strings
           *  (e.g. Spanish "Únete a la membresía de Hustle") never
           *  wrap inside a pill or the row. */}
          <div
            data-testid="platform-header"
            className="sticky top-[var(--global-bar-h)] z-40 w-full bg-[#FAFAF9] dark:bg-stone-900 border-b border-[#EFECE6] dark:border-stone-700"
          >
            <div className="flex items-center gap-x-3 md:gap-x-6 min-h-[60px] md:min-h-[73px] px-4 sm:px-6 xl:pl-10 xl:pr-6">
              <Link
                to={resolvedBrand.to || "/"}
                data-testid="brand-link"
                className="group inline-flex items-center shrink min-w-0"
                aria-label={`${resolvedBrand.name} home`}
              >
                <span className="font-serif-display text-xl sm:text-2xl md:text-3xl font-medium text-[#121614] dark:text-stone-100 group-hover:text-[#C05746] transition-colors leading-tight truncate">
                  {resolvedBrand.name}
                </span>
              </Link>

              {moduleTabs.length > 0 && (
                <>
                  {/* Desktop inline nav (≥ xl) */}
                  <nav
                    data-testid="module-tabs"
                    className="hidden xl:flex items-center gap-x-5 2xl:gap-x-6"
                    aria-label="Module sections"
                  >
                    {moduleTabs.filter((t) => t.align !== "right").map((tab) => {
                      // iter322: mega-menu support in module-host mode
                      if (Array.isArray(tab.children) && tab.children.length > 0) {
                        return (
                          <NavDropdown
                            key={tab.key || tab.label}
                            group={{ label: tab.label, children: tab.children }}
                            location={location}
                          />
                        );
                      }
                      const isExternal = tab.external || /^https?:\/\//i.test(tab.path);
                      const isHashAnchor = typeof tab.path === "string" && tab.path.startsWith("#");
                      const cls = `text-sm tracking-wide font-semibold whitespace-nowrap transition-colors ${
                        !isExternal && !isHashAnchor && location.pathname === tab.path
                          ? "text-[#C05746]"
                          : "text-[#121614] dark:text-stone-100 hover:text-[#C05746]"
                      }`;
                      const tid = `module-tab-${tab.key || tab.path.replace(/[/#]/g, "-")}`;
                      return (isExternal || isHashAnchor) ? (
                        <a key={tab.key || tab.path} href={tab.path} data-testid={tid} className={cls} rel="noopener">
                          {tab.label}
                        </a>
                      ) : (
                        <Link key={tab.key || tab.path} to={tab.path} data-testid={tid} className={cls}>
                          {tab.label}
                        </Link>
                      );
                    })}
                  </nav>

                  {/* Desktop right pills (≥ xl) */}
                  {moduleTabs.some((t) => t.align === "right") && (
                    <nav
                      data-testid="module-tabs-right"
                      className="ml-auto hidden xl:flex items-center gap-2 shrink-0"
                      aria-label="Module sign-in"
                    >
                      {moduleTabs.filter((t) => t.align === "right").map((tab) => {
                        const isExternal = tab.external || /^https?:\/\//i.test(tab.path);
                        const isPrimary = tab.variant === "primary";
                        const cls = isPrimary
                          ? "text-sm tracking-wide font-semibold whitespace-nowrap rounded-full bg-[#C05746] text-white hover:bg-[#A64A3C] transition-colors px-4 py-2"
                          : "text-sm tracking-wide font-semibold whitespace-nowrap rounded-full bg-[#121614] text-[#FAFAF9] hover:bg-[#C05746] transition-colors px-4 py-2";
                        const tid = `module-tab-${tab.key || tab.path.replace(/\//g, "-")}`;
                        return isExternal ? (
                          <a key={tab.key || tab.path} href={tab.path} data-testid={tid} className={cls} rel="noopener">
                            {tab.label}
                          </a>
                        ) : (
                          <Link key={tab.key || tab.path} to={tab.path} data-testid={tid} className={cls}>
                            {tab.label}
                          </Link>
                        );
                      })}
                    </nav>
                  )}

                  {/* Hamburger toggle (< xl) */}
                  <button
                    type="button"
                    data-testid="module-menu-toggle"
                    aria-expanded={mobileOpen}
                    aria-label={mobileOpen ? "Close menu" : "Open menu"}
                    onClick={() => setMobileOpen((v) => !v)}
                    className="ml-auto xl:hidden inline-flex items-center justify-center h-11 w-11 rounded-full hover:bg-[#F3F0EA] dark:hover:bg-stone-800 transition-colors shrink-0"
                  >
                    {mobileOpen ? (
                      <X className="h-5 w-5" strokeWidth={1.5} />
                    ) : (
                      <MenuIcon className="h-5 w-5" strokeWidth={1.5} />
                    )}
                  </button>
                </>
              )}
            </div>

            {/* Mobile / tablet drawer (< xl) — modal sub-menu.
             *  Floats as an absolutely-positioned card anchored to the
             *  bottom-right of the header (directly below the toggle),
             *  above a blurred backdrop so the page content beneath
             *  stays put and slightly de-emphasised. Tapping the
             *  backdrop closes the drawer. */}
            {moduleTabs.length > 0 && mobileOpen && (
              <>
                <button
                  type="button"
                  data-testid="module-mobile-backdrop"
                  aria-label="Close menu"
                  onClick={() => setMobileOpen(false)}
                  className="xl:hidden absolute top-full left-0 right-0 h-screen z-40 bg-stone-900/25 backdrop-blur-md cursor-pointer"
                />
                <div
                  data-testid="module-mobile-nav"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Site menu"
                  className="xl:hidden absolute top-full right-0 z-50 min-w-[220px] max-w-[92vw] bg-[#FAFAF9] dark:bg-stone-900 border border-t-0 border-[#EFECE6] dark:border-stone-700 rounded-bl-2xl rounded-br-none shadow-2xl overflow-hidden"
                >
                  <div className="px-4 py-4 flex flex-col items-end gap-1">
                    {moduleTabs.filter((t) => t.align !== "right").map((tab) => {
                      // iter322: mega-menu flat expansion in mobile drawer —
                      // each child renders as its own row so touch targets
                      // stay large and no dropdown-inside-dropdown is needed.
                      if (Array.isArray(tab.children) && tab.children.length > 0) {
                        return (
                          <div key={tab.key || tab.label} className="w-full flex flex-col items-end gap-1">
                            <div className="px-3 pt-3 pb-1 text-[11px] uppercase tracking-widest text-[#C05746] font-semibold">
                              {tab.label}
                            </div>
                            {tab.children.map((c) => {
                              const active = location.pathname === c.to;
                              const cls = `px-3 py-2 rounded-xl text-[14px] font-semibold text-right whitespace-nowrap transition-colors ${
                                active
                                  ? "bg-[#F3F0EA] dark:bg-stone-800 text-[#C05746]"
                                  : "text-[#121614] dark:text-stone-100 hover:bg-[#F3F0EA] dark:hover:bg-stone-800"
                              }`;
                              return (
                                <Link
                                  key={c.to}
                                  to={c.to}
                                  data-testid={`module-mobile-child-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                                  className={cls}
                                  onClick={() => setMobileOpen(false)}
                                >
                                  {c.label}
                                </Link>
                              );
                            })}
                          </div>
                        );
                      }
                      const isExternal = tab.external || /^https?:\/\//i.test(tab.path);
                      const isHashAnchor = typeof tab.path === "string" && tab.path.startsWith("#");
                      const active = !isExternal && !isHashAnchor && location.pathname === tab.path;
                      const cls = `px-3 py-3 rounded-xl text-[15px] font-semibold text-right whitespace-nowrap transition-colors ${
                        active
                          ? "bg-[#F3F0EA] dark:bg-stone-800 text-[#C05746]"
                          : "text-[#121614] dark:text-stone-100 hover:bg-[#F3F0EA] dark:hover:bg-stone-800"
                      }`;
                      const tid = `module-mobile-tab-${tab.key || tab.path.replace(/[/#]/g, "-")}`;
                      return (isExternal || isHashAnchor) ? (
                        <a key={tab.key || tab.path} href={tab.path} data-testid={tid} className={cls} rel="noopener"
                           onClick={() => setMobileOpen(false)}>
                          {tab.label}
                        </a>
                      ) : (
                        <Link key={tab.key || tab.path} to={tab.path} data-testid={tid} className={cls}
                              onClick={() => setMobileOpen(false)}>
                          {tab.label}
                        </Link>
                      );
                    })}
                    {moduleTabs.some((t) => t.align === "right") && (
                      <div className="mt-3 pt-3 border-t border-[#EFECE6] dark:border-stone-700 w-fit self-end flex flex-col gap-2">
                        {moduleTabs.filter((t) => t.align === "right").map((tab) => {
                          const isExternal = tab.external || /^https?:\/\//i.test(tab.path);
                          const isPrimary = tab.variant === "primary";
                          const cls = isPrimary
                            ? "block w-full text-center px-6 py-3 rounded-full bg-[#C05746] text-white font-semibold whitespace-nowrap hover:bg-[#A64A3C] transition-colors"
                            : "block w-full text-center px-6 py-3 rounded-full bg-[#121614] text-[#FAFAF9] font-semibold whitespace-nowrap hover:bg-[#C05746] transition-colors";
                          const tid = `module-mobile-tab-${tab.key || tab.path.replace(/\//g, "-")}`;
                          return isExternal ? (
                            <a key={tab.key || tab.path} href={tab.path} data-testid={tid} className={cls} rel="noopener">
                              {tab.label}
                            </a>
                          ) : (
                            <Link key={tab.key || tab.path} to={tab.path} data-testid={tid} className={cls}>
                              {tab.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      ) : (
      <header
        data-testid="platform-header"
        className="sticky top-[var(--global-bar-h)] z-40 bg-[#FDFBF7]/95 dark:bg-stone-900/95 backdrop-blur-xl border-b transition-all duration-300 border-[#E8E5DF] dark:border-stone-700"
      >
        <div className={`${maxWidth} mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20`}>
          <Link
            to={resolvedBrand.to || "/"}
            data-testid="brand-link"
            className="flex items-center gap-2 group shrink-0"
            aria-label={`${resolvedBrand.name} home`}
          >
            {resolvedBrand.logoSrc && (
              <img
                src={resolvedBrand.logoSrc}
                alt={resolvedBrand.name}
                className="h-10 w-10 sm:h-11 sm:w-11 object-contain"
              />
            )}
            {resolvedBrand.subtitle ? (
              <span className="hidden sm:flex flex-col leading-tight">
                <span className="font-bold text-[#121614] dark:text-stone-100 text-[15px] tracking-tight">
                  {resolvedBrand.name}
                </span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500 dark:text-stone-400 font-semibold">
                  {resolvedBrand.subtitle}
                </span>
              </span>
            ) : (
              <span className="font-serif-display text-2xl md:text-3xl font-medium text-[#121614] dark:text-stone-100 group-hover:text-[#C05746] transition-colors">
                {resolvedBrand.name}
              </span>
            )}
          </Link>

          {extras && <div className="flex items-center gap-2">{extras}</div>}

          <nav className="hidden md:flex items-center gap-8" data-testid="primary-nav">
            {NAV.map((n, i) =>
              Array.isArray(n.children) ? (
                <NavDropdown key={`grp-${i}-${n.label}`} group={n} location={location} />
              ) : (
                <Tip key={n.to} label={`Go to ${n.label}`}>
                <Link
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  aria-label={n.label}
                  className={`text-sm tracking-wide transition-colors ${
                    location.pathname === n.to
                      ? "text-[#C05746]"
                      : "text-[#121614] dark:text-stone-100 hover:text-[#C05746]"
                  }`}
                >
                  {n.label}
                </Link>
                </Tip>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            {/* NOTE (iter 247): Emergency, Language, Theme, Modules AND
             *  Account are ALL rendered ONCE at the app shell level by
             *  <GoogleTranslateBar/>. Rule #4 — do NOT re-mount them
             *  here (previous versions had 4 duplicate rows of chrome).
             *  showModules / showAccount props are kept for storefronts
             *  that DON'T show the global suite bar. */}
            <GrandfatherLockBadge />
            {showCart && (
              <Tip label={t("nav_cart") || "Open cart"}>
              <button
                type="button"
                onClick={() => setCartOpen(true)}
                data-testid="open-cart-button"
                className="relative inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-[#F3F0EA] dark:hover:bg-stone-800 transition-colors"
                aria-label="Open cart"
              >
                <ShoppingBag className="h-5 w-5 text-[#121614] dark:text-stone-100" strokeWidth={1.5} />
                {totalItems > 0 && (
                  <span
                    data-testid="cart-badge"
                    className="absolute -top-0.5 -right-0.5 bg-[#C05746] text-white text-[10px] font-semibold rounded-full h-5 w-5 flex items-center justify-center"
                  >
                    {totalItems}
                  </span>
                )}
              </button>
              </Tip>
            )}

            <Tip label={mobileOpen ? "Close menu" : "Open menu"}>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-[#F3F0EA] dark:hover:bg-stone-800 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-5 w-5" strokeWidth={1.5} /> : <MenuIcon className="h-5 w-5" strokeWidth={1.5} />}
            </button>
            </Tip>

            {resolvedShowBookCta && (
              <Link to={resolvedCtaLink} className="hidden md:inline-flex">
                <Button
                  data-testid="header-book-button"
                  className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-6"
                >
                  {resolvedCtaLabel}
                </Button>
              </Link>
            )}
          </div>
        </div>

        {mobileOpen && (
          <div data-testid="mobile-nav" className="md:hidden border-t border-[#E8E5DF] dark:border-stone-700 bg-[#FDFBF7] dark:bg-stone-900">
            <div className="px-4 py-4 flex flex-col gap-2">
              {NAV.map((n, i) =>
                Array.isArray(n.children) ? (
                  <div key={`mgrp-${i}-${n.label}`} className="mt-2">
                    <p className="px-3 pb-1 text-[10px] uppercase tracking-[0.15em] font-bold text-[#5A7247]">
                      {n.label}
                    </p>
                    {n.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`mobile-nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="px-3 py-2.5 rounded-xl text-[#121614] dark:text-stone-100 hover:bg-[#F3F0EA] dark:hover:bg-stone-800 transition-colors block"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="px-3 py-3 rounded-xl text-[#121614] dark:text-stone-100 hover:bg-[#F3F0EA] dark:hover:bg-stone-800 transition-colors"
                  >
                    {n.label}
                  </Link>
                )
              )}
              {resolvedShowBookCta && (
                <Link to={resolvedCtaLink} className="mt-2">
                  <Button
                    data-testid="mobile-book-button"
                    className="w-full bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full"
                  >
                    {resolvedCtaLabel}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}

        {belowContent && (
          <div className={`${maxWidth} mx-auto px-4 sm:px-6 lg:px-8 pb-3`}>
            {belowContent}
          </div>
        )}
      </header>
      )}

      {showCart && <CartSheet open={cartOpen} onOpenChange={setCartOpen} />}
    </>
  );
}
